import { getSubAmount, formatRenewalDate } from "./billingFormat";

/* How the active subscription row reads on the plan card. */

export const planFlags = (activeSub) => {
  const isVerified = activeSub?.subscription_type === "verified";
  return {
    isVerified,
    isScheduledCancel:
      isVerified &&
      activeSub?.status === "active" &&
      activeSub?.recurring_status === "cancelled",
    isExpired: isVerified && activeSub?.status === "inactive",
    /* Transcribed discriminator: the row's own amount says which cycle it is
       (99 monthly, 999 annual), because both share subscription_type. */
    isAnnual: isVerified && Number(activeSub?.amount) >= 500,
  };
};

/* Transcribed capitalisation: `(card_brand || "").replace(/^./, upper)`. */
const titleCase = (value) => String(value || "").replace(/^./, (c) => c.toUpperCase());

export const planPresentation = (activeSub, { portalLoading = false, hidePlan = false } = {}) => {
  const { isVerified, isScheduledCancel, isExpired, isAnnual } = planFlags(activeSub);
  const hasPlan = Boolean(activeSub?.subscription_type);

  /* Transcribed: no subscription_type on the surfaced row is the "nothing
     active" card — a title and a way to buy, nothing else. */
  if (!hasPlan) {
    return {
      planName: "No active subscription",
      statusLabel: null,
      cadence: null,
      cadenceTone: "normal",
      card: undefined,
      currency: "AED",
      amount: undefined,
      period: null,
      meta: null,
      credits: null,
      action: hidePlan ? null : { label: "Buy a plan", kind: "navigate" },
    };
  }

  const suffix = isExpired ? " (Expired)" : isScheduledCancel ? " (Cancel pending)" : "";

  const planName = isVerified
    ? `Membership — ${isAnnual ? "Annual" : "Monthly"}${suffix}`
    : activeSub?.subscription_type;

  /* Transcribed: the cadence line is suppressed entirely for pay-as-you-go. */
  let cadence = null;
  if (activeSub?.subscription_type !== "pay-as-you-go") {
    if (isVerified) {
      cadence = isExpired
        ? `Expired on ${formatRenewalDate(activeSub?.date_of_end)}`
        : isScheduledCancel
        ? `Cancellation scheduled — keeps benefits until ${formatRenewalDate(activeSub?.date_of_end)}`
        : isAnnual
        ? "Billed annually — auto-renews"
        : "Billed monthly — auto-renews";
    } else {
      cadence = "pay on a monthly subscription";
    }
  }

  /* The meta line, as a descriptor rather than a node so this stays pure.
     `renew` marks the expired case, whose copy carries a link back into the
     Stripe portal. */
  const meta = isVerified
    ? isExpired
      ? { renew: true, label: "Verification badge revoked." }
      : {
          label: isScheduledCancel ? "Expires on:" : "Next renewal on:",
          value: formatRenewalDate(activeSub?.date_of_end),
        }
    : {
        label: "Last Claim :",
        /* Transcribed verbatim, unformatted date included. */
        value: activeSub?.date_of_end || "No Subscription",
      };

  /* Transcribed: verified subscriptions are a per-company badge and track no
     per-profile credits, so that legacy line is hidden for them. */
  const credits = isVerified
    ? null
    : { label: "No. of profile credits remaining :", value: activeSub?.no_of_profile_views || 0 };

  const action = hidePlan
    ? null
    : isVerified
    ? {
        kind: "portal",
        label: portalLoading
          ? "Redirecting…"
          : isExpired
          ? "Renew today"
          : "Manage subscription",
        disabled: portalLoading,
      }
    : { kind: "navigate", label: "Modify plan" };

  return {
    planName,
    /* The badge stays through a scheduled cancellation and goes when the plan
       has actually expired — transcribed from the three-state model. */
    statusLabel: isVerified && !isExpired ? "Active" : null,
    cadence,
    cadenceTone: isScheduledCancel || isExpired ? "warning" : "normal",
    card:
      isVerified && activeSub?.card_last4
        ? { brand: titleCase(activeSub?.card_brand), last4: activeSub.card_last4 }
        : undefined,
    /* Transcribed: verified prices are AED, everything else is the legacy
       dollar figure from the plan table. */
    currency: isVerified ? "AED" : "$",
    amount: isVerified ? Number(activeSub?.amount) || 0 : getSubAmount(activeSub?.subscription_type) || 0,
    period: isAnnual ? "per year" : "per month",
    meta,
    credits,
    action,
  };
};

export default planPresentation;
